import React, { useEffect } from 'react'
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import MagazineStageSimple from '../../Components/MagazineStageSimple/MagazineStageSimple';
import imgCapa from '../../assets/news-events/hydraulic_logistics_mining/Capa.png';
import imgImagem1 from '../../assets/news-events/hydraulic_logistics_mining/Imagem 1.png';
import imgImagem2 from '../../assets/news-events/hydraulic_logistics_mining/Imagem 2.png';
import imgImagem3 from '../../assets/news-events/hydraulic_logistics_mining/Imagem 3.png';
import imgImagem4 from '../../assets/news-events/hydraulic_logistics_mining/Imagem 4.png';
import imgImagem5 from '../../assets/news-events/hydraulic_logistics_mining/Imagem 5.png';
import MagazineTextBlock from '../../Components/MagazineTextBlock/MagazineTextBlock';

const HydraulicLogisticsMining = () => {
    useEffect(() => {
        window.scrollTo(0, 0); // Scroll to top on component mount
    }, []);

    const { t } = useTranslation();
    const ogTitle = t('Hydraulic logistics in mining: stock without shortages');
    const ogDescription = t('Understand how to organize hydraulic stock in mining operations using the ABC Curve, criticality criteria, and HANSA-FLEX logistics models such as branches, in-company stores, service containers, and inventory management.');
    const ogImage = `https://www.hansa-flex.com.br/${imgCapa}`;
    const ogUrl = `https://www.hansa-flex.com.br/hydraulic-logistics-mining`;

    return (
        <div className="chh-article">
            <Helmet>
                <meta property="og:title" content={ogTitle} />
                <meta property="og:description" content={ogDescription} />
                <meta property="og:image" content={ogImage} />
                <meta property="og:url" content={ogUrl} />
                <meta property="og:type" content="News" />
            </Helmet>

            <MagazineStageSimple
                imageSrc={imgCapa}
                imagePosition="afterTitle"
                imageAlt="Hydraulic logistics in mining: stock without shortages"
                title={"Hydraulic logistics in mining: stock without shortages"}
                text={[
                    "In a mining operation, a small part can cause an enormous problem. A hose, fitting, or adapter that is not available at the right moment can leave equipment idle for hours or even days. And when the operation is far from major centers, an emergency replacement becomes even more difficult.",
                    "Therefore, organizing hydraulic stock does not simply mean buying more parts. It means understanding what really needs to be available, in what quantity, and how often it should be replenished.",
                ]}
                readingTimeMinutes={8}
            />

            <MagazineTextBlock
                title="The challenge of keeping parts available in mining"
                paragraphs={[
                    "The mining routine is demanding. Equipment works under pressure, dust, vibration, and heavy loads. In this scenario, failures and wear are part of the operation.",
                    "The problem begins when the team identifies the failure but cannot find the correct part in stock. From there, <0>emergency purchases, expensive freight, improvisations, and idle machines</0> arise. In remote operations, this impact can be even greater, since transport times are usually longer.",
                    "On the other hand, <0>keeping too many parts is not a good solution either</0>. Excessive stock takes up space, ties up capital, and increases the risk of loss due to inadequate storage.",
                    "The goal should be to find the balance: having the right items available without turning the warehouse into a depot of rarely used materials. This is where the <0>ABC Curve</0> can help.",
                ]}
            />

            <MagazineTextBlock
                imageSrc={imgImagem1}
                imagePosition="beforeTitle"
                imageAlt="How the ABC Curve organizes hydraulic stock"
                title="How the ABC Curve organizes hydraulic stock"
                paragraphs={[
                    "The ABC Curve is a practical way to separate stock items according to their importance to the company. The methodology is based on the Pareto principle, known as the 80/20 rule. In general, a small portion of items usually represents a significant share of the value handled.",
                    "In practice, materials are divided into three groups:",
                ]}
                bulletPoints={[
                    "Class A: items of higher value, consumption, or importance. They require close monitoring, guaranteed minimum stock, and planned replenishment.",
                    "Class B: items of intermediate importance. They should be reviewed periodically but do not require the same level of control as Class A items.",
                    "Class C: items of lower value or consumption. They can have simpler and more flexible management.",
                ]}
            />

            <MagazineTextBlock
                imageSrc={imgImagem2}
                imagePosition="beforeTitle"
                imageAlt="Price cannot be the only criterion in mining"
                paragraphs={[
                    "In mining, however, price cannot be the only criterion.",
                    "A low-cost fitting, for example, can halt an essential machine if there is no compatible alternative. Therefore, the classification must also consider:",
                ]}
                bulletPoints={[
                    "the impact of equipment downtime;",
                    "frequency of failures;",
                    "replenishment lead time;",
                    "possibility of substitution;",
                    "distance between the supplier and the operation.",
                ]}
                paragraphs2={[
                    "Thus, an inexpensive item may be treated as critical if its absence compromises production.",
                ]}
            />

            <MagazineTextBlock
                title="How to build a more efficient hydraulic stock"
                paragraphs={[
                    "The first step is to identify which equipment is essential for the continuity of the operation. Then, it is important to analyze real data, such as maintenance orders, failure history, parts consumption, and warehouse withdrawals.",
                    "With this information, the company can define minimum stock levels and replenishment points more consistent with its reality. Some simple actions make a difference, such as <0>standardizing materials, organizing storage locations, monitoring consumption, and reviewing stock regularly.</0>",
                ]}
            />

            <MagazineTextBlock
                imageSrc={imgImagem3}
                imagePosition="afterTitle"
                imageAlt="When stock needs to be closer to the operation"
                title="When stock needs to be closer to the operation"
                paragraphs={[
                    "In some projects, organizing the central warehouse is not enough. The distance between the stock and the equipment continues to generate delays. In these situations, HANSA-FLEX can support the operation with different hydraulic logistics models:",
                ]}
            />

            <MagazineTextBlock
                title="1. Branches strategically distributed across the country"
                links={["https://www.hansa-flex.com.br/contact-us/"]}
                paragraphs={[
                    "HANSA-FLEX has <1>9 branches strategically located across Brazil</1>, with robust stock and its own logistics system. When the operation requires special components, the items can also be brought directly from the headquarters in Germany.",
                ]}
            />

            <MagazineTextBlock
                title="2. In-company store"
                links={["https://www.hansa-flex.com.br/Worksinworks"]}
                paragraphs={[
                    "With the <1>In-Company Store</1>, materials, components, and specialized professionals are available inside your operation. The structure makes it possible to <0>produce hydraulic hoses and supply the necessary components according to demand</0>, reducing travel, waiting times, and storage costs.",
                    "The service can also include training for employees, with practical guidance on the assembly and correct installation of the products. In this way, the team gains more autonomy, reduces failures, and increases the reliability of the services performed.",
                ]}
            />

            <MagazineTextBlock
                imageSrc={imgImagem4}
                imagePosition="afterTitle"
                imageAlt="Hydraulic Service Container"
                title="3. Hydraulic Service Container"
                links={["https://www.hansa-flex.com.br/Hydraulicworkshopcontainers"]}
                paragraphs={[
                    "The <1>container workshops</1> work as <0>a stock and a workshop inside the project itself</0>. The structure can bring together machines, tools, components, and even a bench for high-pressure testing.",
                    "Each container can be configured according to the needs of your operation, with standardized organization of the materials and the possibility of planned replenishment.",
                    "We can also offer on-site training, preparing employees to carry out the pre-assembly of hoses and components in accordance with the applicable standards.",
                ]}
            />

            <MagazineTextBlock
                title="4. Inventory Management"
                links={[
                    "https://www.hansa-flex.com.br/Warehousemanagement",
                    "https://www.hansa-flex.com.br/Scannersolutions",
                    "https://www.hansa-flex.com.br/Kitting",
                ]}
                paragraphs={[
                    "<1>HANSA-FLEX helps you review and organize these processes.</1> The work may include creating a standardized list of materials, identifying the most important items, and defining suitable locations for each component.",
                    "Storage conditions are also assessed, since dust, liquids, temperature variations, and impacts can compromise the quality and service life of the parts.",
                    "In addition, when necessary, the operation can adopt solutions such as <2>scanner ordering</2> or <3>kits</3> prepared for specific maintenance activities.",
                    "The objective is simple: reduce search time, avoid shortages, and make the flow of materials more predictable.",
                ]}
            />

            <MagazineTextBlock
                imageSrc={imgImagem5}
                imagePosition="beforeTitle"
                imageAlt="The right stock protects productivity"
                title="The right stock protects productivity"
                links={["https://hansaflexbrasil.com.br/fale-conosco/"]}
                paragraphs={[
                    "Good inventory management does not seek to accumulate the largest possible number of parts. It seeks to ensure that the truly important components are available when they are needed.",
                    "By combining the right assets, your operation reduces emergency purchases, avoids improvisations, and improves the predictability of maintenance.",
                    "<1>Talk to HANSA-FLEX</1><0> and find the most suitable hydraulic logistics solution for your mining operation.</0>",
                ]}
            />

            <div style={{ backgroundColor: '#f8f8f8' }}>
                <br />
                <br />
                <br />
            </div>

        </div>
    )
}

export default HydraulicLogisticsMining
